import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Play, X, SkipForward, Sparkles } from 'lucide-react';
import { useLab, resolveLabChildId, getCourseTemplateForChild } from '../LabContext';

// Asset paths — files will be dropped in /public/assets/intro/ later.
// If they are missing, the gate still renders a calm placeholder card
// instead of popping a broken <video>.
const POSTER_URL = '/assets/intro/nimny-course-intro-poster.webp';
const VIDEO_URL_BY_LANG = {
  ru: '/assets/intro/nimny-course-intro-ru.mp4',
  en: '/assets/intro/nimny-course-intro-en.mp4',
};

// Per-student × per-course localStorage flag.
const flagKey = (studentId, courseTemplate) =>
  `nimnyLab:introSeen:${studentId}:${courseTemplate}:v1`;

// Window event the "Watch welcome again" button dispatches. Keeps the gate
// self-contained — pages don't need to import a context provider.
export const INTRO_REPLAY_EVENT = 'nimny-lab:open-intro';
export const openIntroVideo = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(INTRO_REPLAY_EVENT));
  }
};

// Lightweight HEAD check — used to suppress auto-show before the video file
// exists. Cached at module scope so we don't probe more than once per load.
// We also verify the Content-Type because dev servers (CRA) and many SPA
// hosts (Vercel, Netlify with rewrites) fall back to serving index.html
// with HTTP 200 for missing static files, which would otherwise make this
// probe always think the asset exists.
const assetProbe = {};
const probeAsset = (url) => {
  if (assetProbe[url] !== undefined) return Promise.resolve(assetProbe[url]);
  return fetch(url, { method: 'HEAD' })
    .then((r) => {
      if (!r.ok) {
        assetProbe[url] = false;
        return false;
      }
      const ct = (r.headers.get('content-type') || '').toLowerCase();
      const isMedia =
        ct.startsWith('video/') ||
        ct.startsWith('image/') ||
        ct === 'application/octet-stream';
      assetProbe[url] = isMedia;
      return isMedia;
    })
    .catch(() => {
      assetProbe[url] = false;
      return false;
    });
};

const IntroVideoGate = () => {
  const { user, language, tx } = useLab();
  const location = useLocation();
  const videoRef = useRef(null);

  const isChild = user && user.role !== 'parent' && user.role !== 'super_admin';
  const childId = resolveLabChildId({
    routeStudentId: undefined,
    pathname: location.pathname,
    user,
  });
  const courseTemplate = isChild ? getCourseTemplateForChild(user, childId) : null;
  const studentId = isChild ? childId : null;

  const [open, setOpen] = useState(false);
  const [hasAssets, setHasAssets] = useState(null); // null = unknown, true/false after probe
  const [videoFailed, setVideoFailed] = useState(false);

  // Pick video URL by current language with English fallback.
  const videoUrl = VIDEO_URL_BY_LANG[language] || VIDEO_URL_BY_LANG.en;

  // Probe poster/video once we know we are on a child route.
  useEffect(() => {
    if (!isChild || !studentId || !courseTemplate) return;
    let cancelled = false;
    Promise.all([probeAsset(POSTER_URL), probeAsset(videoUrl)]).then(([p, v]) => {
      if (cancelled) return;
      setHasAssets(Boolean(p || v));
    });
    return () => {
      cancelled = true;
    };
  }, [isChild, studentId, courseTemplate, videoUrl]);

  // Auto-open on first child visit when assets are reachable.
  useEffect(() => {
    if (!isChild || !studentId || !courseTemplate) return;
    if (hasAssets !== true) return;
    let seen = false;
    try {
      seen = localStorage.getItem(flagKey(studentId, courseTemplate)) === 'true';
    } catch (_) {
      seen = false;
    }
    if (!seen) setOpen(true);
  }, [isChild, studentId, courseTemplate, hasAssets]);

  // Manual "Watch welcome again" — always honored, even when assets are
  // still missing (we then show a friendly "coming soon" card).
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(INTRO_REPLAY_EVENT, handler);
    return () => window.removeEventListener(INTRO_REPLAY_EVENT, handler);
  }, []);

  const markSeen = useCallback(() => {
    if (!studentId || !courseTemplate) return;
    try {
      localStorage.setItem(flagKey(studentId, courseTemplate), 'true');
    } catch (_) {
      /* localStorage might be blocked in incognito — ignore. */
    }
  }, [studentId, courseTemplate]);

  const close = useCallback(
    (markAsSeen = true) => {
      try {
        const v = videoRef.current;
        if (v) {
          v.pause();
          v.currentTime = 0;
        }
      } catch (_) {
        /* defensive — video may not be mounted */
      }
      if (markAsSeen) markSeen();
      setOpen(false);
    },
    [markSeen]
  );

  const start = useCallback(() => {
    setVideoFailed(false);
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // Autoplay blocked — leave the native controls visible so the
        // child can tap Play directly on the player.
      });
    }
  }, []);

  // Bilingual strings — kept local so we don't have to extend the global
  // translations file for one component.
  const tt = (ru, en) => (language === 'ru' ? ru : en);

  if (!isChild || !open) return null;

  const assetsMissing = hasAssets === false;
  const showVideo = hasAssets === true && !videoFailed;

  return (
    <div
      className="lab-intro-gate"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lab-intro-title"
      data-testid="intro-video-gate"
    >
      <div className="lab-intro-backdrop" onClick={() => close(true)} />
      <div className="lab-intro-card" data-testid="intro-video-card">
        <button
          type="button"
          className="lab-intro-close"
          onClick={() => close(true)}
          aria-label={tt('Закрыть', 'Close')}
          data-testid="intro-video-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="lab-intro-header">
          <div className="lab-intro-spark">
            <Sparkles className="w-[18px] h-[18px]" />
          </div>
          <h2 id="lab-intro-title" className="lab-intro-title">
            {tt('Добро пожаловать в курс Нимни', "Welcome to Nimny's Course")}
          </h2>
          <p className="lab-intro-sub">
            {tt(
              'Посмотри короткое приветствие перед началом приключения.',
              'Watch a short welcome before your adventure begins.'
            )}
          </p>
        </div>

        <div className="lab-intro-media">
          {showVideo ? (
            <video
              ref={videoRef}
              className="lab-intro-video"
              src={videoUrl}
              poster={POSTER_URL}
              controls
              playsInline
              preload="none"
              onEnded={() => close(true)}
              onError={(e) => {
                // Ignore the spurious "empty src" / "no source loaded" error
                // that fires before the user has tapped Play with
                // preload="none". A real load failure shows up as
                // MEDIA_ERR_NETWORK/DECODE on the <video> element AFTER play
                // was attempted.
                const v = e.currentTarget;
                if (v && v.error && v.networkState !== 0) {
                  setVideoFailed(true);
                }
              }}
              data-testid="intro-video-player"
            />
          ) : (
            <div className="lab-intro-placeholder" data-testid="intro-video-placeholder">
              <img
                src="/assets/print/nimny/nimny_01_lantern_standing.webp"
                alt=""
                aria-hidden="true"
                className="lab-intro-mascot"
              />
              <p className="lab-intro-placeholder-text">
                {assetsMissing
                  ? tt(
                      'Приветственное видео скоро появится. Нажми «Начать», чтобы продолжить.',
                      'A welcome video is coming soon. Tap “Start” to continue.'
                    )
                  : tt(
                      'Не получилось загрузить видео. Можно начать без него.',
                      "We couldn't load the video. You can start without it."
                    )}
              </p>
            </div>
          )}
        </div>

        <div className="lab-intro-actions">
          <button
            type="button"
            className="lab-intro-btn lab-intro-btn-primary"
            onClick={() => {
              if (showVideo) {
                start();
              } else {
                close(true);
              }
            }}
            data-testid="intro-video-start"
          >
            <Play className="w-4 h-4" />
            {tt('Начать', 'Start')}
          </button>
          <button
            type="button"
            className="lab-intro-btn lab-intro-btn-ghost"
            onClick={() => close(true)}
            data-testid="intro-video-skip"
          >
            <SkipForward className="w-4 h-4" />
            {tt('Пропустить', 'Skip')}
          </button>
        </div>
      </div>

      <style>{INTRO_CSS}</style>
    </div>
  );
};

const INTRO_CSS = `
  .lab-intro-gate {
    position: fixed;
    inset: 0;
    z-index: 1100;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
  }
  .lab-intro-backdrop {
    position: absolute;
    inset: 0;
    background: radial-gradient(ellipse at center, rgba(8,4,16,0.72) 0%, rgba(0,0,0,0.92) 100%);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
  }
  .lab-intro-card {
    position: relative;
    width: 100%;
    max-width: 760px;
    background: linear-gradient(180deg, rgba(28,18,8,0.96) 0%, rgba(14,9,4,0.98) 100%);
    border: 1px solid rgba(243,217,164,0.28);
    border-radius: 18px;
    padding: 22px 22px 20px;
    color: #f5ebe0;
    box-shadow:
      0 0 0 1px rgba(243,217,164,0.08),
      0 20px 60px rgba(0,0,0,0.55),
      0 0 80px rgba(243,217,164,0.06);
    max-height: calc(100vh - 32px);
    overflow-y: auto;
  }
  .lab-intro-close {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    border: 1px solid rgba(243,217,164,0.22);
    background: rgba(243,217,164,0.04);
    color: rgba(243,217,164,0.85);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.18s ease;
  }
  .lab-intro-close:hover { background: rgba(243,217,164,0.10); }

  .lab-intro-header { text-align: center; padding: 4px 24px 14px; }
  .lab-intro-spark {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 999px;
    border: 1px solid rgba(243,217,164,0.28);
    background: rgba(243,217,164,0.08);
    color: #f3d9a4;
    margin-bottom: 8px;
  }
  .lab-intro-title {
    font-family: var(--font-heading, 'Lora', serif);
    font-size: 22px;
    line-height: 1.2;
    color: #f3d9a4;
    margin: 0 0 6px;
  }
  .lab-intro-sub {
    font-family: 'Nunito', 'Inter', system-ui, sans-serif;
    font-size: 14px;
    color: rgba(245,235,224,0.78);
    margin: 0;
  }

  .lab-intro-media {
    margin-top: 4px;
    border-radius: 12px;
    overflow: hidden;
    background: #0a0612;
    border: 1px solid rgba(243,217,164,0.18);
    aspect-ratio: 16 / 9;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .lab-intro-video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #000;
  }
  .lab-intro-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 18px 22px;
    text-align: center;
  }
  .lab-intro-mascot {
    width: 96px;
    height: auto;
    opacity: 0.92;
    filter: drop-shadow(0 0 24px rgba(243,217,164,0.18));
  }
  .lab-intro-placeholder-text {
    font-family: 'Nunito', sans-serif;
    font-size: 14px;
    color: rgba(245,235,224,0.82);
    margin: 0;
    max-width: 36ch;
  }

  .lab-intro-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-top: 16px;
    flex-wrap: wrap;
  }
  .lab-intro-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 22px;
    border-radius: 999px;
    font-family: 'Nunito', sans-serif;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.18s ease, background 0.18s ease, border-color 0.18s ease;
    min-height: 44px;
  }
  .lab-intro-btn:active { transform: scale(0.97); }
  .lab-intro-btn-primary {
    background: linear-gradient(180deg, #f3d9a4 0%, #d4ad60 100%);
    border: 1px solid rgba(243,217,164,0.6);
    color: #1a0f04;
    box-shadow: 0 6px 20px rgba(243,217,164,0.22);
  }
  .lab-intro-btn-primary:hover { filter: brightness(1.04); }
  .lab-intro-btn-ghost {
    background: rgba(243,217,164,0.05);
    border: 1px solid rgba(243,217,164,0.25);
    color: #f5ebe0;
  }
  .lab-intro-btn-ghost:hover { background: rgba(243,217,164,0.10); }

  @media (max-width: 640px) {
    .lab-intro-gate { padding: 10px; }
    .lab-intro-card { padding: 18px 16px 16px; border-radius: 14px; }
    .lab-intro-title { font-size: 19px; }
    .lab-intro-sub { font-size: 13px; }
    .lab-intro-header { padding: 0 8px 12px; }
    .lab-intro-btn { width: 100%; justify-content: center; }
    .lab-intro-actions { flex-direction: column; gap: 8px; }
  }
`;

export default IntroVideoGate;
